import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Space, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authFetch } from '../../utils/api';
import { SubscriptionAuditFilterSchema, auditFilterDefaults, auditQueryFilters } from '../../schemas/subscription_audit';
import type { AuditQueryFilters, SubscriptionAuditFilterDTO } from '../../schemas/subscription_audit';
import { SubscriptionScope, ReadError } from './common';
import { subscriptionRequest } from './api';
import { LatestAuditTask, SubscriptionAuditPageSchema, auditPath, completeAuditCsv, saveAuditCsv } from './audit_contracts';
import type { SubscriptionAuditPage } from './audit_contracts';
import type { Capabilities } from './contracts';
import { AuditFilters, AuditPartialNotice, AuditTable } from './audit_view';

type AuditRequest = (path: string, signal: AbortSignal) => Promise<unknown>;
type AuditExportRequest = (path: string, signal: AbortSignal) => Promise<Response>;
const requestPage: AuditRequest = (path, signal) => subscriptionRequest(path, SubscriptionAuditPageSchema, { signal });
const requestExport: AuditExportRequest = (path, signal) => authFetch(path, { signal });
interface AuditContentProps {
  brand: string;
  capabilities: Pick<Capabilities, 'canReadSubscriptions'> | null;
  refresh?: number;
  request?: AuditRequest;
  exportRequest?: AuditExportRequest;
  save?: (blob: Blob) => void;
}

/** Exported to exercise the real page with local synthetic fixtures. */
export function AuditContent({ brand, capabilities, refresh = 0, ...requests }: AuditContentProps) {
  if (!capabilities) return <Alert type="info" showIcon message="正在確認訂閱查詢權限" />;
  if (!capabilities.canReadSubscriptions) return <Alert type="warning" showIcon message="此帳號沒有訂閱查詢權限" />;
  return <AuditQuery key={`${brand}:${refresh}`} brand={brand} {...requests} />;
}

function AuditQuery({ brand, request = requestPage, exportRequest = requestExport, save = saveAuditCsv }: Pick<AuditContentProps, 'brand' | 'request' | 'exportRequest' | 'save'>) {
  const form = useForm<SubscriptionAuditFilterDTO>({ resolver: zodResolver(SubscriptionAuditFilterSchema), mode: 'onBlur', defaultValues: { ...auditFilterDefaults } });
  const [filters, setFilters] = useState<AuditQueryFilters>({});
  const [page, setPage] = useState<{ cursor?: string; snapshot?: string; previous: (string | undefined)[] }>({ previous: [] });
  const [revision, setRevision] = useState(0);
  const path = auditPath(brand, filters, page);
  const key = `${path}:${revision}`;
  const [result, setResult] = useState<{ key: string; data: SubscriptionAuditPage | null; loading: boolean; error: string | null }>();
  const listTask = useRef(new LatestAuditTask());
  const exportTask = useRef(new LatestAuditTask());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const state = result?.key === key ? result : { data: null, loading: true, error: null };

  const cancelExport = () => { exportTask.current.cancel(); setExporting(false); };
  useEffect(() => {
    const task = listTask.current.replace();
    exportTask.current.cancel();
    setExporting(false);
    setExportError(null);
    setExportNotice(null);
    setResult({ key, data: null, loading: true, error: null });
    request(path, task.signal).then(raw => {
      if (!task.isCurrent()) return;
      const data = SubscriptionAuditPageSchema.parse(raw);
      if (data.items.some(row => row.brand !== brand)) throw new Error('查詢結果與指定品牌不符');
      setResult({ key, data, loading: false, error: null });
    }).catch(cause => {
      if (task.isCurrent()) setResult({ key, data: null, loading: false, error: cause instanceof Error ? cause.message : '訂閱審計查詢失敗' });
    });
    return () => listTask.current.cancel();
  }, [brand, key, path, request]);
  useEffect(() => {
    const watch = form.watch(() => { exportTask.current.cancel(); setExporting(false); setExportNotice(null); });
    return () => { watch.unsubscribe(); listTask.current.cancel(); exportTask.current.cancel(); };
  }, [form]);

  const apply = (values: SubscriptionAuditFilterDTO) => {
    listTask.current.cancel(); cancelExport();
    setFilters(auditQueryFilters(values));
    setPage({ previous: [] });
    setRevision(value => value + 1);
    form.reset(values);
  };
  const reset = () => { form.reset({ ...auditFilterDefaults }); apply({ ...auditFilterDefaults }); };
  const move = (direction: 'next' | 'previous' | 'first') => {
    if (!state.data) return;
    listTask.current.cancel(); cancelExport();
    if (direction === 'first') { setPage({ previous: [] }); setRevision(value => value + 1); }
    if (direction === 'next' && state.data.nextCursor) setPage({ cursor: state.data.nextCursor, snapshot: state.data.snapshot, previous: [...page.previous, page.cursor] });
    if (direction === 'previous' && page.previous.length) setPage({ cursor: page.previous.at(-1), snapshot: state.data.snapshot, previous: page.previous.slice(0, -1) });
  };
  const download = async () => {
    if (!state.data || state.loading || form.formState.isDirty) return;
    const task = exportTask.current.replace();
    setExporting(true); setExportError(null); setExportNotice(null);
    try {
      const response = await exportRequest(auditPath(brand, filters, { snapshot: state.data.snapshot }, true), task.signal);
      const blob = await completeAuditCsv(response, task.signal);
      if (!task.isCurrent()) return;
      save(blob);
      setExportNotice('已匯出全部符合查詢條件的紀錄');
    } catch (cause) {
      if (task.isCurrent()) setExportError(cause instanceof Error ? cause.message : '匯出失敗，請重新查詢後再試');
    } finally { if (task.isCurrent()) setExporting(false); }
  };

  return <>
    <Card title="篩選訂閱審計紀錄"><AuditFilters form={form} onApply={apply} onReset={reset} loading={state.loading} /></Card>
    <Card title={<Space wrap>審計紀錄<Tag>第 {page.previous.length + 1} 頁 · 每頁 50 筆</Tag></Space>} extra={<Space wrap>
      {exporting && <Button onClick={() => { cancelExport(); setExportNotice('已取消匯出'); }}>取消匯出</Button>}
      <Button icon={<DownloadOutlined />} loading={exporting} disabled={!state.data || state.loading || form.formState.isDirty} onClick={download}>匯出 CSV</Button>
    </Space>}>
      <Typography.Paragraph type="secondary">依事件時間由新到舊；支付訂閱以成功發放時間為準。CSV 匯出全部符合已套用條件的紀錄，不限本頁 50 筆。</Typography.Paragraph>
      {form.formState.isDirty && <Alert type="info" showIcon message="篩選條件尚未套用，請按「查詢紀錄」更新清單後再匯出" style={{ marginBottom: 16 }} />}
      <ReadError error={state.error} />
      {exportError && <Alert type="error" showIcon message="CSV 匯出失敗" description={exportError} style={{ marginBottom: 16 }} />}
      {exportNotice && <Alert type="info" showIcon message={exportNotice} style={{ marginBottom: 16 }} />}
      {state.data?.dataStatus === 'PARTIAL' && <AuditPartialNotice />}
      <AuditTable items={state.data?.items ?? []} loading={state.loading} />
      <Space wrap className="subscription-pagination">
        <Button disabled={state.loading || !state.data} onClick={() => move('first')}>回到最新 50 筆</Button>
        <Button disabled={state.loading || !page.previous.length} onClick={() => move('previous')}>上一頁</Button>
        <Button disabled={state.loading || !state.data?.nextCursor} onClick={() => move('next')}>下一頁</Button>
        {state.data && <Typography.Text type="secondary">本頁 {state.data.items.length} 筆 · 查詢時間 {new Date(state.data.generatedAt).toLocaleString()}</Typography.Text>}
      </Space>
    </Card>
  </>;
}

export function SubscriptionAuditPage() {
  return <SubscriptionScope title="訂閱審計紀錄" description="查詢會員支付訂閱、人工發放與人工撤銷的時間、來源及異動紀錄。">{scope => <AuditContent {...scope} />}</SubscriptionScope>;
}
